import { FCElement } from '@fc/component';
import { patchDOM } from '@fc/dom-patch';
import { FCEvents } from '@fc/events';
import { sectionManager } from '@fc/sections';
import { importHTMLNode, isAbortError, LatestRequestController } from '@fc/section-response';

const MARKET_CONTEXT_QUERY_KEYS = ['country', 'locale'];

// Fetches and displays local pickup availability per variant.
class LocalPickup extends FCElement {
  #listenerController = null;
  #requestController = new LatestRequestController();
  #ownerSection = null;
  #statusRegion = null;
  #statusFrame = null;
  #lastAnnouncement = '';

  connectedCallback() {
    super.connectedCallback();

    this.#statusRegion = this.#resolveStatusRegion();
    this.#ownerSection = this.#resolveOwnerSection();
    if (!this.#ownerSection) return;

    this.#listenerController?.abort();
    this.#listenerController = new AbortController();

    this.#ownerSection.addEventListener(FCEvents.variantChange, this.#handleVariantChange, {
      signal: this.#listenerController.signal,
      passive: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortInFlightRequest();
    this.#listenerController?.abort();
    this.#cancelStatusAnnouncement();
    this.#listenerController = null;
    this.#ownerSection = null;
    this.#statusRegion = null;
  }

  #resolveOwnerSection() {
    return this.closest('.shopify-section, dialog');
  }

  #handleVariantChange = (event) => {
    const payload = this.#parseVariantPayload(event);
    if (!payload) return;
    const previousVariantId = this.dataset.variantId;

    if (payload.newProductId) {
      this.dataset.productId = payload.newProductId;
    } else if (payload.productId && this.dataset.productId && payload.productId !== this.dataset.productId) {
      return;
    }

    if (payload.productUrl) {
      this.dataset.productUrl = payload.productUrl;
    }

    if (!payload.variantId) {
      this.dataset.variantId = '';
      this.#abortInFlightRequest();
      this.#hideAvailability({ announce: true, variantTitle: payload.variantTitle });
      return;
    }

    if (payload.variantId === previousVariantId) {
      return;
    }

    this.dataset.variantId = payload.variantId;

    if (!payload.available) {
      this.#abortInFlightRequest();
      this.#hideAvailability({ announce: true, variantTitle: payload.variantTitle });
      return;
    }

    this.removeAttribute('hidden');
    this.#requestAvailability(payload.variantId, payload.variantTitle);
  };

  #parseVariantPayload(event) {
    const detail = event?.detail;
    if (!detail) return null;

    const variant = detail.resource ?? null;
    const newProduct = detail.data?.newProduct ?? null;
    // eslint-disable-next-line eqeqeq
    const variantId = variant?.id == null ? null : String(variant.id);
    const available = Boolean(variant?.available);
    const variantTitle = typeof variant?.title === 'string' ? variant.title : '';
    const productUrl = newProduct?.url;
    const productId = this.#safeString(
      newProduct?.id ?? detail.data?.productId ?? (event.target instanceof HTMLElement ? event.target.dataset.productId : null),
    );

    return {
      variantId,
      available,
      variantTitle,
      productId,
      newProductId: this.#safeString(newProduct?.id),
      productUrl: typeof productUrl === 'string' ? productUrl : null,
    };
  }

  #hideAvailability({ announce = false, variantTitle = '' } = {}) {
    this.setAttribute('hidden', '');
    if (announce) {
      this.#announceAvailability(this.#fallbackAvailabilityMessage(variantTitle));
    }
  }

  #abortInFlightRequest() {
    this.#requestController.abort();
  }

  #requestAvailability = async (variantId, variantTitle = '') => {
    const requestUrl = this.#buildAvailabilityRequestUrl(variantId);
    if (!requestUrl) return;

    const request = this.#requestController.start();

    try {
      const doc = await sectionManager.getSectionDocument(this.dataset.sectionId, {
        cache: false,
        signal: request.signal,
        url: requestUrl,
      });

      if (!request.isCurrent()) return;

      const nextNode = importHTMLNode(doc, `store-pickup[data-variant-id="${variantId}"]`);
      if (nextNode) {
        this.removeAttribute('hidden');
        patchDOM(this, nextNode);
        this.#announceAvailability(this.#currentAvailabilityMessage(variantTitle));
        return;
      }

      this.#hideAvailability({ announce: true, variantTitle });
    } catch (error) {
      if (!isAbortError(error) && request.isCurrent()) {
        this.#hideAvailability();
      }
    } finally {
      request.finish();
    }
  };

  #buildAvailabilityRequestUrl(variantId) {
    const { productUrl, sectionId } = this.dataset;
    if (!productUrl || !sectionId || !variantId) return null;

    const url = new URL(productUrl, window.location.origin);
    this.#preserveMarketContext(url);
    url.searchParams.set('variant', variantId);
    url.hash = '';
    return url;
  }

  #preserveMarketContext(url) {
    const current = new URL(window.location.href);

    for (const key of MARKET_CONTEXT_QUERY_KEYS) {
      const value = current.searchParams.get(key);
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }

    return url;
  }

  #resolveStatusRegion() {
    const targetId = this.dataset.statusTarget;
    if (targetId) {
      const target = document.getElementById(targetId);
      if (target instanceof HTMLElement) return target;
    }

    const nextSibling = this.nextElementSibling;
    if (nextSibling instanceof HTMLElement && nextSibling.dataset.ref === 'pickupStatus') {
      return nextSibling;
    }

    const region = document.createElement('span');
    if (targetId) region.id = targetId;
    region.className = 'visually-hidden';
    region.dataset.ref = 'pickupStatus';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    this.after(region);
    return region;
  }

  #currentAvailabilityMessage(variantTitle = '') {
    const parts = [];
    const normalizedVariantTitle = this.#normalizeText(variantTitle);
    const summary = this.#availabilitySummaryText();

    if (normalizedVariantTitle && normalizedVariantTitle !== 'Default Title') {
      parts.push(normalizedVariantTitle);
    }
    if (summary) {
      parts.push(summary);
    }

    return this.#sentence(parts.join('. '));
  }

  #fallbackAvailabilityMessage(variantTitle = '') {
    const parts = [];
    const normalizedVariantTitle = this.#normalizeText(variantTitle);
    const unavailableLabel = this.#normalizeText(this.dataset.pickupUnavailableLabel) || 'No pickup';

    if (normalizedVariantTitle && normalizedVariantTitle !== 'Default Title') {
      parts.push(normalizedVariantTitle);
    }
    parts.push(unavailableLabel);

    return this.#sentence(parts.join('. '));
  }

  #availabilitySummaryText() {
    const summaryNodes = this.querySelectorAll(
      '.pickup-availability__row .fit-pickup__text-sm, .pickup-availability__row .fit-pickup__text-xs',
    );
    return Array.from(summaryNodes)
      .map((node) => this.#normalizeText(node.textContent))
      .filter(Boolean)
      .join('. ');
  }

  #announceAvailability(message) {
    const normalizedMessage = this.#normalizeText(message);
    if (!normalizedMessage || normalizedMessage === this.#lastAnnouncement) return;

    const region = this.#statusRegion ?? this.#resolveStatusRegion();
    this.#statusRegion = region;
    this.#lastAnnouncement = normalizedMessage;
    this.#cancelStatusAnnouncement();
    region.textContent = '';

    this.#statusFrame = requestAnimationFrame(() => {
      this.#statusFrame = null;
      if (this.isConnected) {
        region.textContent = normalizedMessage;
      }
    });
  }

  #cancelStatusAnnouncement() {
    if (this.#statusFrame == null) return;
    cancelAnimationFrame(this.#statusFrame);
    this.#statusFrame = null;
  }

  #safeString(value) {
    // eslint-disable-next-line eqeqeq
    return value == null || value === '' ? null : String(value);
  }

  #normalizeText(value) {
    return String(value ?? '').replace(/\s+/g, ' ').trim();
  }

  #sentence(value) {
    const normalized = this.#normalizeText(value);
    if (!normalized) return '';
    return /[.!?]$/.test(normalized) ? normalized : `${normalized}.`;
  }
}

if (!customElements.get('store-pickup')) {
  customElements.define('store-pickup', LocalPickup);
}
